import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IMammadabaMapProps, OnGameCompletedCallback, OnGameLocationChangeCallback } from "../mapProvider";
import { coordinatesConverter, EpsgCode } from "@geo/js";
import { ICoords } from "../GeoLocation";
import { Map, Feature } from "ol";
import { LineString, Point } from "ol/geom";
import { FeatureType, GamePointStatus, LocationState } from "../enums";
import { useSimulator } from "./userSimulator";
import { useAppContext } from "../AppContext";

export interface IGameUserPosition {
    location: number[];
    approachingFeatures: Feature[];
    leavingFeatures: Feature[];
    distances: { objectId: number; distance: number }[];
}

let onGameLocationChange: MutableRefObject<OnGameLocationChangeCallback>;
let onGameCompleted: MutableRefObject<OnGameCompletedCallback>;

export const useGameLocation = (map: Map, props: IMammadabaMapProps): void => {
    const { setLocationState, position, setPosition } = useAppContext();

    const [checkPoints, setCheckPoints] = useState<Feature[]>([]);
    const [approaching, setApproaching] = useState<Feature[]>([]);
    const [leaving, setLeaving] = useState<Feature[]>([]);

    onGameLocationChange = useRef<OnGameLocationChangeCallback>(props.onGameLocationChange);
    onGameLocationChange.current = useMemo(() => props.onGameLocationChange, [props.onGameLocationChange]);
    onGameCompleted = useRef<OnGameCompletedCallback>(props.onGameCompleted);
    onGameCompleted.current = useMemo(() => props.onGameCompleted, [props.onGameCompleted]);

    const leaveRadius = props.gameLeavingRadius || 60;
    const approachRadius = props.gameApproachingRadius || 50;

    useEffect(() => {
        if (props.startGame) {
            filterCurrentGameFeatures();
            startLocate();
            return;
        }
        stopLocate();
    }, [props.startGame, props.useGameSimulator]);

    useEffect(() => {
        props.gameCompleted && stopLocate();
    }, [props.gameCompleted]);

    useEffect(() => {
        if (position.latitude === 0 && position.longitude === 0) {
            return;
        }

        const location = [position.latitude, position.longitude];
        const { approachingFeatures, leavingFeatures, distances } = calculateGameState(location);

        onGameLocationChange.current?.({ location, approachingFeatures, leavingFeatures, distances });
    }, [position]);

    const calculateGameState = useCallback(
        (location) => {
            const smallRing = [];
            const bigRing = [];
            const distances = [];
            checkPoints.map((point) => {
                const line = new LineString([(point.getGeometry() as Point).getCoordinates(), location]);
                const length = line.getLength();
                distances.push({ pointId: point.get("POINTID"), distance: Math.floor(length) });
                if (length < leaveRadius) {
                    bigRing.push(point);
                    length < approachRadius && smallRing.push(point);
                }
            });

            const approachingFeatures = smallRing.filter((a) => !approaching.includes(a));
            const leavingFeatures = leaving.filter((a) => !bigRing.includes(a));

            setLeaving(bigRing);
            setApproaching(smallRing);

            approachingFeatures.length &&
                approachingFeatures.map(
                    (f) => f.get("GAMEPOINTSTATUS") !== GamePointStatus.Completed && f.set("APPROACHED", true)
                );
            leavingFeatures.length && leavingFeatures.map((f) => f.set("APPROACHED", false));

            const completed = !checkPoints.some((point) => point.get("GAMEPOINTSTATUS") !== GamePointStatus.Completed);
            completed && onGameCompleted.current?.(props.startGame);

            return { approachingFeatures, leavingFeatures, distances };
        },
        [checkPoints, approaching, leaving]
    );

    const filterCurrentGameFeatures = () => {
        const gameId = props.startGame.get("GAMEID");
        const filteredCheckPoints = props.gameLayers
            .filter((features) => features.some((f) => f.get("GAMEPOINTTYPE") === FeatureType.GameCheckPoint))[0]
            .filter((feature) => feature.get("GAMEID") === gameId);
        setCheckPoints(filteredCheckPoints);
    };

    const startLocate = () =>
        props.useGameSimulator && map
            ? useSimulator(map, true, approachRadius, leaveRadius, onSuccess)
            : setLocationState(LocationState.Waiting);

    const stopLocate = () =>
        props.useGameSimulator && map ? useSimulator(map, false) : setLocationState(LocationState.Inactive);

    const onSuccess = (position: ICoords, isLKScoords?: boolean) => {
        if (isLKScoords) {
            setPosition({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                heading: position.coords.heading
            });
            return;
        }
        const point = coordinatesConverter.convert(
            [position.coords.longitude, position.coords.latitude],
            EpsgCode.Wgs84,
            EpsgCode.WebMercator
        );

        setPosition({ latitude: point[1], longitude: point[0], heading: position.coords.heading });
    };
};
